import { Component, OnInit, Signal, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { locale as portuguese } from './i18n/pt';
import { locale as english } from './i18n/en';
import { locale as spanish } from './i18n/es';
import { FuseConfigService } from '@fuse/services/config';
import { ConvertHelper } from 'app/core/helpers/convert.helper';
import { ErrorHelper } from 'app/core/helpers/error.helper';
import { MoneyService } from 'app/core/services/money/money.service';
import { OrderService } from 'app/core/services/order/order.service';
import { FuseTranslationLoaderService } from 'app/core/translate/translation-loader.service';
import { BaseComponent } from 'app/layout/common/base/base.component';

import { MatIconModule } from '@angular/material/icon';
import { IOrderRating } from 'app/core/models/order/rating';
import { LoadingComponent, ModalComponent } from '@kiwid-app/kiwid-ui-lib-angular';
import { first } from 'rxjs';

@Component({
    selector: 'app-modal-rating',
    templateUrl: './modal-rating.component.html',
    styleUrls: ['./modal-rating.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatIconModule, TranslatePipe, LoadingComponent],
})
export class ModalRatingComponent extends BaseComponent implements OnInit {
    readonly translateService = inject(TranslateService);
    readonly title = inject(Title);
    readonly moneyService = inject(MoneyService);
    private readonly orderService = inject(OrderService);

    public dialog: Signal<MatDialogRef<ModalComponent, string>>;
    public orderId: Signal<string>;
    private readonly orderRating = signal<IOrderRating>(null);
    protected readonly renderOrderRating = computed(() => {
        const orderRating = this.orderRating();
        return {
            note: orderRating.note,
            ratingValue: ConvertHelper.convertToMaskMoney(
                orderRating?.ratingValue ?? 0,
                {
                    decimalSeparator: this.moneyService.maskMoney.decimalSeparator,
                    thousandSeparator: this.moneyService.maskMoney.thousandSeparator,
                    prefix: '',
                },
                1,
            ),
            ratings: orderRating.ratings.map((rating) => {
                const percentage: string[] = [];
                const value = rating.value ?? 0;

                for (let i = 1; i <= 5; i++) {
                    percentage.push(value > i ? '100%' : `${(i - value > 1 ? 0 : (i - value - 1) * -1 * 100).toFixed(2)}%`);
                }

                return {
                    ...rating,
                    percentage,
                };
            }),
        };
    });
    protected readonly isLoading = signal(true);

    ngOnInit(): void {
        this.registerOnTranslate('ORDER_DETAIL_MODAL_RATING', portuguese, english, spanish);
        this.getOrderRating();
    }

    get ratingValue(): string {
        return ConvertHelper.convertToMaskMoney(
            this.orderRating()?.ratingValue ?? 0,
            {
                decimalSeparator: this.moneyService.maskMoney.decimalSeparator,
                thousandSeparator: this.moneyService.maskMoney.thousandSeparator,
                prefix: '',
            },
            1,
        );
    }

    getRatingStars(value: number): string[] {
        const percentage: string[] = [];
        const rating = value ?? 0;

        for (let i = 1; i <= 5; i++) {
            percentage.push(rating > i ? '100%' : `${(i - rating > 1 ? 0 : (i - rating - 1) * -1 * 100).toFixed(2)}%`);
        }

        return percentage;
    }

    getOrderRating(): void {
        this.subscriptions.add(
            this.orderService
                .getOrderRating(this.orderId())
                .pipe(first())
                .subscribe({
                    next: ({ data }) => {
                        this.orderRating.set(data);
                        this.isLoading.set(false);
                    },
                    error: (error) => {
                        this.isLoading.set(false);
                        ErrorHelper.setToastrErrorModal(this.dialog().componentInstance, error);
                    },
                }),
        );
    }
}
